const URL_REGEX = /(https?:\/\/[^\s]+)/gi
const MARKDOWN_LINK_REGEX = /\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g
const IMAGE_REGEX = /\.(png|jpg|jpeg|gif|webp|bmp|svg)(\?.*)?$/i
const IMAGE_CONTENT_TYPE_REGEX = /^image\/(jpeg|jpg|png|gif|webp|bmp|svg\+xml|svg)/i
const REQUEST_TIMEOUT_MS = 5000
const TRAILING_PUNCTUATION_REGEX = /[)\]}>",.!?:;。，、！？；：」』）》】]$/u
const PAIRED_CLOSERS = {
  ')': '(',
  ']': '[',
  '}': '{',
  '>': '<',
  '」': '「',
  '』': '『',
  '》': '《',
  '】': '【',
}
const UNCONDITIONAL_TRAILING_CHARACTERS = new Set([
  '"',
  ',',
  '.',
  '!',
  '?',
  ':',
  ';',
  '。',
  '，',
  '、',
  '！',
  '？',
  '；',
  '：',
])

function countCharacter(value, character) {
  return Array.from(value).filter(item => item === character).length
}

function shouldTrimTrailingCharacter(url, character) {
  if (UNCONDITIONAL_TRAILING_CHARACTERS.has(character)) {
    return true
  }

  const openingCharacter = PAIRED_CLOSERS[character]
  if (!openingCharacter) {
    return false
  }

  return countCharacter(url, character) > countCharacter(url, openingCharacter)
}

function splitUrlAndTrailingText(rawUrl) {
  let normalizedUrl = rawUrl
  let trailingText = ''

  while (normalizedUrl) {
    const trailingCharacter = normalizedUrl.slice(-1)

    if (!TRAILING_PUNCTUATION_REGEX.test(trailingCharacter)) {
      break
    }

    if (!shouldTrimTrailingCharacter(normalizedUrl, trailingCharacter)) {
      break
    }

    trailingText = trailingCharacter + trailingText
    normalizedUrl = normalizedUrl.slice(0, -1)
  }

  return {
    normalizedUrl,
    trailingText,
  }
}

async function checkImageByContentType(url) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timeoutId = controller
    ? setTimeout(() => {
        controller.abort()
      }, REQUEST_TIMEOUT_MS)
    : null

  try {
    const response = await fetch(url, {
      method: 'HEAD',
      signal: controller ? controller.signal : undefined,
    })
    const contentType = response.headers.get('content-type') || ''
    return IMAGE_CONTENT_TYPE_REGEX.test(contentType)
  } catch (error) {
    return false
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}

/**
 * 第一階段：解析 Markdown 連結格式 [text](url)，
 * 將內容拆分為純文字片段與 Markdown 連結片段。
 */
function splitMarkdownLinks(content) {
  const fragments = []
  let lastIndex = 0
  const matches = Array.from(content.matchAll(MARKDOWN_LINK_REGEX))

  matches.forEach(match => {
    const fullMatch = match[0]
    const label = match[1]
    const url = match[2]
    const index = match.index || 0

    if (index > lastIndex) {
      fragments.push({ raw: content.slice(lastIndex, index), isMarkdownLink: false })
    }

    fragments.push({ raw: fullMatch, isMarkdownLink: true, label, url })
    lastIndex = index + fullMatch.length
  })

  if (lastIndex < content.length) {
    fragments.push({ raw: content.slice(lastIndex), isMarkdownLink: false })
  }

  return fragments.length ? fragments : [{ raw: content, isMarkdownLink: false }]
}

/**
 * 第二階段：對純文字片段進行裸 URL 偵測，
 * Markdown 連結片段則直接根據副檔名判斷 image/link。
 */
export function parseChatMessage(content, options = {}) {
  const { convertImageLinks = false } = options

  if (!content) {
    return []
  }

  const segments = []
  const fragments = splitMarkdownLinks(content)

  fragments.forEach(fragment => {
    // Markdown 連結片段：直接判斷圖片或一般連結
    if (fragment.isMarkdownLink) {
      const isImage = convertImageLinks && IMAGE_REGEX.test(fragment.url)
      segments.push({
        type: isImage ? 'image' : 'link',
        content: fragment.url,
        ...(fragment.label ? { label: fragment.label } : {}),
      })
      return
    }

    // 純文字片段：偵測裸 URL
    const text = fragment.raw
    let lastIndex = 0
    const matches = Array.from(text.matchAll(URL_REGEX))

    matches.forEach(match => {
      const rawUrl = match[0]
      const { normalizedUrl: url, trailingText } = splitUrlAndTrailingText(rawUrl)
      const index = match.index || 0

      if (index > lastIndex) {
        segments.push({
          type: 'text',
          content: text.slice(lastIndex, index),
        })
      }

      if (url) {
        segments.push({
          type: convertImageLinks && IMAGE_REGEX.test(url) ? 'image' : 'link',
          content: url,
        })
      }

      if (trailingText) {
        segments.push({
          type: 'text',
          content: trailingText,
        })
      }

      lastIndex = index + rawUrl.length
    })

    if (lastIndex < text.length) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex),
      })
    }
  })

  return segments.length ? segments : [{ type: 'text', content }]
}

export async function resolveChatMessageSegments(content, options = {}) {
  const { convertImageLinks = false } = options
  const segments = parseChatMessage(content, options)

  if (!convertImageLinks) {
    return segments
  }

  const resolvedSegments = await Promise.all(
    segments.map(async segment => {
      if (segment.type !== 'link') {
        return segment
      }

      const isImageByHeader = await checkImageByContentType(segment.content)
      if (isImageByHeader) {
        return {
          type: 'image',
          content: segment.content,
          ...(segment.label ? { label: segment.label } : {}),
        }
      }

      return segment
    })
  )

  return resolvedSegments
}
